import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { Dispatch, SetStateAction } from 'react';
import { useExportPriceList, useUserProfile } from '~/services/shop/hooks';
import { useTranslations } from '~/services/umbraco/hooks';
import { Button } from '~/shared/components/Button';
import { Modal } from '~/shared/components/Modal';
import { SkeletonLoader } from '~/shared/components/SkeletonLoader';
import { Text } from '~/shared/components/Text';

type Props = {
    setShowUserAdminModal: Dispatch<SetStateAction<boolean>>;
    showUserAdminModal: boolean;
};

export default function UserAdminModal({ showUserAdminModal, setShowUserAdminModal }: Props) {
    const { translate } = useTranslations();
    const { data } = useUserProfile();
    const { isError, isLoading } = useExportPriceList();

    const userEmail = translate('mit-fog.userProfile.accountInformation.exportText').replace(
        '[USER_EMAIL]',
        data?.user?.email ?? '',
    );

    return (
        <Modal
            isOpen={showUserAdminModal}
            handleClose={() => setShowUserAdminModal(false)}
            type="miniModal"
            noPadding
        >
            <Container>
                {isLoading ? (
                    <Skeleton />
                ) : (
                    <>
                        {isError ? (
                            <>
                                <Text variant="display3">
                                    {translate('mit-fog.userProfile.accountInformation.error')}
                                </Text>

                                <Text variant="paragraph">
                                    {translate(
                                        'mit-fog.userProfile.accountInformation.error.subtitle',
                                    )}
                                </Text>

                                <ButtonWrapper>
                                    <Button
                                        variant="fill"
                                        onClick={() => setShowUserAdminModal(false)}
                                        aria-label={translate(
                                            'mit-fog.userProfile.accountInformation.ok',
                                        )}
                                    >
                                        {translate('mit-fog.userProfile.accountInformation.ok')}
                                    </Button>
                                </ButtonWrapper>
                            </>
                        ) : (
                            <>
                                <Text variant="display3">
                                    {translate('mit-fog.userProfile.accountInformation.request')}
                                </Text>

                                <Text variant="paragraph">{userEmail}</Text>

                                <ButtonWrapper>
                                    <Button
                                        variant="fill"
                                        onClick={() => setShowUserAdminModal(false)}
                                        aria-label={translate(
                                            'mit-fog.userProfile.accountInformation.ok',
                                        )}
                                    >
                                        {translate('mit-fog.userProfile.accountInformation.ok')}
                                    </Button>
                                </ButtonWrapper>
                            </>
                        )}
                    </>
                )}
            </Container>
        </Modal>
    );
}

function Skeleton() {
    return (
        <>
            <SkeletonLoader height={30} width={240} />
            <SkeletonLoader height={40} isFullWidth style={{ marginBottom: 40 }} />
            <SkeletonLoader height={40} isFullWidth />
        </>
    );
}

const Container = styled(motion.div)(({ theme }) => ({
    display: 'grid',
    gridRowGap: theme.space[4],
    padding: `${theme.space[8]} ${theme.space[6]} ${theme.space[4]}`,
}));

const ButtonWrapper = styled.div(({ theme }) => ({
    display: 'flex',
    gap: theme.space[2],
    justifyContent: 'space-between',
    marginTop: theme.space[6],
    ['> button']: {
        flex: 1,
    },
    ['> a']: {
        flex: 1,
    },
}));
